import { NewUser, validateUser } from "./userValidation";

export interface CsvParseResult {
  users: NewUser[];
  errors: string[];
}

export const parseCsvData = (csvText: string): CsvParseResult => {
  const lines = csvText.split("\n").filter((line) => line.trim());

  if (lines.length < 2) {
    return {
      users: [],
      errors: ["CSV file must contain a header row and at least one data row"],
    };
  }

  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const expectedHeaders = [
    "first_name",
    "last_name",
    "rank",
    "badge_number",
    "department",
    "email",
    "is_admin",
  ];

  const missingHeaders = expectedHeaders.filter((h) => !headers.includes(h));
  if (missingHeaders.length > 0) {
    return {
      users: [],
      errors: [`Missing required columns: ${missingHeaders.join(", ")}`],
    };
  }

  const users: NewUser[] = [];
  const errors: string[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(",").map((v) => v.trim());
    // Excel and friends drop the trailing comma when the last cell is
    // blank; tolerate that for the optional trailing vendor-ID column.
    if (
      values.length === headers.length - 1 &&
      headers[headers.length - 1] === "bodycam_external_user_id"
    ) {
      values.push("");
    }
    if (values.length !== headers.length) {
      errors.push(`Row ${i + 1}: Incorrect number of columns`);
      continue;
    }

    // Optional column: only present in newer templates. A blank cell means
    // "no vendor ID" and the field is simply omitted from the row we submit
    // (the backend treats an omitted field as "preserve any staged ID").
    const bodycamVendorId =
      headers.includes("bodycam_external_user_id")
        ? values[headers.indexOf("bodycam_external_user_id")] || ""
        : "";

    const user: NewUser = {
      firstName: values[headers.indexOf("first_name")] || "",
      lastName: values[headers.indexOf("last_name")] || "",
      position: values[headers.indexOf("rank")] || "",
      badgeNumber: values[headers.indexOf("badge_number")] || "",
      department: values[headers.indexOf("department")] || "",
      email: values[headers.indexOf("email")] || "",
      isAdmin: ["true", "1", "yes", "y"].includes(
        (values[headers.indexOf("is_admin")] || "").toLowerCase()
      ),
      ...(bodycamVendorId ? { bodycamVendorId } : {}),
    };

    const userErrors = validateUser(user);
    if (userErrors.length > 0) {
      errors.push(`Row ${i + 1}: ${userErrors.join(", ")}`);
    } else {
      users.push(user);
    }
  }

  return { users, errors };
};

export const downloadCsvTemplate = (): void => {
  const headers = [
    "first_name",
    "last_name",
    "rank",
    "badge_number",
    "department",
    "email",
    "is_admin",
    "bodycam_external_user_id",
  ];
  // bodycam_external_user_id is optional: the officer's ID in the station's
  // bodycam vendor system (Axon/WatchGuard/LensLock). Leave it blank for
  // users without a camera.
  const csvContent =
    headers.join(",") +
    "\n" +
    "John,Doe,CHIEF,12345,Patrol,john.doe@dept.gov,true,\n" +
    "Jane,Smith,SERGEANT,12346,IT,jane.smith@dept.gov,false,officer-vendor-id";

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "user_template.csv";
  a.click();
  window.URL.revokeObjectURL(url);
};
